import { useEffect, useState } from "react";
import { IoEye } from "react-icons/io5";
import { MdDelete, MdEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import AddCategoryModal from "../../../Components/Modals/Category/AddCategory";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { useCategory } from "./useHook";
import Swal from "sweetalert2";
import Loader, { ImageWithFallback } from "../../../Utils/loader";

function Categories() {
  const [modalOpen, setModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [categoryNameQuery, setCategoryNameQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const {
    addCategory,
    editCategory,
    getCategoriesWithPagination,
    updateCategoryStatus,
    searchCategories,
    deleteCategory,
  } = useCategory();
  const navigate = useNavigate();

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const fetchCategories = async (currentOffset = offset, showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }
    try {
      await getCategoriesWithPagination(
        setCategories,
        setTotal,
        currentOffset,
        limit,
        categoryNameQuery,
        statusFilter
      );
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setOffset(0);
      const response = await searchCategories(categoryNameQuery, statusFilter, 0, limit);
      setCategories(response.categories);
      const totalRecords = response.pagination?.totalRecords || response.categories.length;
      setTotal(totalRecords);
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCategoryNameQuery("");
    setStatusFilter("");
    setOffset(0);
    fetchCategories(0);
  };

  useEffect(() => {
    fetchCategories();
  }, [offset]);

  const handlePageChange = (newOffset) => {
    setIsLoading(true);
    setOffset(newOffset);
    fetchCategories(newOffset, false);
  };

  const handleSaveCategory = async (categoryData, id = null) => {
    setIsLoading(true);
    try {
      let response;

      if (id) {
        response = await editCategory(id, categoryData);
        if (response) {
          await fetchCategories(offset);
        }
      } else {
        response = await addCategory(categoryData);
        if (response) {
          setOffset(0);
          await fetchCategories(0);
        }
      }

      if (response) {
        toggleModal();
        Swal.fire("Success", "Category saved successfully!", "success");
      }
    } catch (error) {
      console.error("Error in handleSaveCategory:", error);
      Swal.fire("Error", error.message || "Failed to save category.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setCurrentCategory(category);
    toggleModal();
  };

  const handleToggleStatus = async (id, currentStatus) => {
    setIsLoading(true);
    try {
      const newStatus = !currentStatus;
      const response = await updateCategoryStatus(id, newStatus);

      if (response) {
        setCategories((prevCategories) =>
          prevCategories.map((category) =>
            category.id === id ? { ...category, status: newStatus } : category
          )
        );

        Swal.fire({
          title: "Success",
          text: `Category status has been ${newStatus ? "activated" : "deactivated"
            } successfully!`,
          icon: "success",
          timer: 1000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to update category status.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewPage = (id) => {
    navigate("/category/view", { state: { id } });
  };

  const handleDeleteCategory = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });
    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        const response = await deleteCategory(id);
        if (response) {
          await fetchCategories(offset);
          Swal.fire("Success", "Category deleted successfully!", "success");
        }
      } catch (error) {
        console.error("Error in handleDeleteCategory:", error);
        Swal.fire("Error", error.message || "Failed to delete category.", "error");
      } finally {
        setIsLoading(false);
      }
    }
  }

  if (isLoading) {
    return <Loader message="Loading categories..." />;
  }

  return (
    <div>
      <div className="bg-gray-100 p-4 flex justify-between">
        <h2 className="text-2xl font-bold">Categories</h2>
        <button
          className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
          onClick={() => {
            setCurrentCategory(null);
            toggleModal();
          }}
        >
          Add Category
        </button>
      </div>

      <div className="mt-4">
        <div className="bg-white p-4 rounded-md shadow-md mb-6 flex gap-4 mt-4">
          <input
            type="text"
            className="w-[40%] flex-grow px-4 py-2 border rounded-lg"
            placeholder="Search by category name"
            value={categoryNameQuery}
            onChange={(e) => setCategoryNameQuery(e.target.value)}
          />
          <select
            className="w-[20%] px-4 py-2 border rounded-lg"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="" className="opacity-25">
              Select Status
            </option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button
            className="w-full sm:w-[45%] md:w-60 bg-blue-500 text-white px-4 py-2 rounded-md"
            onClick={handleSearch}
          >
            Search
          </button>
          <button
            className="w-full sm:w-[45%] md:w-60 bg-primary-dark text-white px-4 py-2 rounded-md"
            onClick={handleReset}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="overflow-x-auto mt-6">
        <table className="min-w-full table-auto bg-white shadow-md rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-gradient-to-r from-primary-dark to-primary-light text-white">
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Sr. No.
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Image
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Category Name
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Product Count
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Status
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="text-center py-10 text-gray-500 text-lg font-medium"
                >
                  No data found
                </td>
              </tr>
            ) : (
              categories.map((category, index) => (
                <tr
                  key={category.id}
                  className={`${index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                >
                  <td className="px-4 py-2">{offset + index + 1}</td>
                  <td className="px-4 py-2">
                    <ImageWithFallback
                      src={category.image}
                      alt={category.name}
                      className="w-16 h-16 object-cover rounded-full shadow-lg"
                    />
                  </td>
                  <td className="px-4 py-2">{category.name}</td>
                  <td className="px-6 py-4">{category.productCount}</td>
                  <td className="px-6 py-4">
                    <div
                      className={`relative inline-block w-16 h-8 rounded-full transition-colors duration-300 cursor-pointer ${category.status ? "bg-primary-dark" : "bg-[#cccccc]"
                        }`}
                      onClick={() =>
                        handleToggleStatus(category.id, category.status)
                      }
                    >
                      <div
                        className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transform transition-transform duration-300 ${category.status ? "translate-x-8" : ""
                          }`}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 flex space-x-4">
                    <button
                      className="bg-primary-dark text-white py-2 px-4 rounded-lg shadow-md flex items-center"
                      onClick={() => handleEditCategory(category)}
                    >
                      <MdEdit />
                    </button>
                    <button
                      className="bg-blue-500 text-white py-2 px-4 rounded-lg shadow-md flex items-center"
                      onClick={() => handleViewPage(category.id)}
                    >
                      <IoEye />
                    </button>
                    <button
                      className="bg-red-500 text-white py-2 px-4 rounded-lg shadow-md flex items-center"
                      onClick={() => handleDeleteCategory(category.id)}
                    >
                      <MdDelete />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > limit && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}

      <AddCategoryModal
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        category={currentCategory}
        handleSaveCategory={handleSaveCategory}
      />
    </div>
  );
}

export default Categories;
